import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { EntShell, Btn, Field, inputCls, Kicker, errText } from "@/components/enterprise/Ent";

export default function EnterpriseProposal() {
  const [params] = useSearchParams();
  const [cat, setCat] = useState(null);
  const [f, setF] = useState({
    org_name: "", org_type: "", contact_name: "", email: "", phone: "", option: "partnership",
    participants_estimate: "", departments: "", program_interest: params.get("program") ? [params.get("program")] : [],
    objectives: [], timeline: "", details: "",
  });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  useEffect(() => {
    document.title = "Request a proposal | WasntMe.Party Enterprise";
    api.get("/enterprise/catalog").then((r) => setCat(r.data));
  }, []);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const toggle = (k, id) => setF({ ...f, [k]: f[k].includes(id) ? f[k].filter((x) => x !== id) : [...f[k], id] });

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try { await api.post("/enterprise/proposals", f); setDone(true); }
    catch (e2) { setErr(errText(e2)); } finally { setBusy(false); }
  };

  if (done) return (
    <EntShell><section className="max-w-xl mx-auto px-6 py-20" role="status">
      <Kicker>Request received</Kicker>
      <h1 className="font-display text-4xl mt-3" data-testid="proposal-done">Thank you. We'll be in touch.</h1>
      <p className="mt-4 text-[15px] text-[#3b3530]">A member of our team will reply to {f.email} to discuss scope, deliverables, and next steps.</p>
    </section></EntShell>
  );

  return (
    <EntShell>
      <form onSubmit={submit} className="max-w-3xl mx-auto px-6 py-14 space-y-6" data-testid="proposal-form">
        <div>
          <Kicker>Custom Enterprise Partnership</Kicker>
          <h1 className="font-display text-4xl sm:text-5xl mt-3">Request a proposal.</h1>
          <p className="mt-3 text-[15px] text-[#3b3530]">Use this form for larger groups, several departments, recurring programs, or custom deliverables. Please share public information only.</p>
        </div>
        <div className="bg-white border border-[#ddd5c9] rounded-lg p-6 grid sm:grid-cols-2 gap-4">
          <Field id="p-org" label="Organization name"><input id="p-org" required className={inputCls} value={f.org_name} onChange={set("org_name")} data-testid="proposal-org" /></Field>
          <Field id="p-type" label="Organization type">
            <select id="p-type" className={inputCls} value={f.org_type} onChange={set("org_type")} data-testid="proposal-type">
              <option value="">Select…</option>{["Corporation", "Government agency", "Educational institution", "Nonprofit", "Other"].map((o) => <option key={o}>{o}</option>)}
            </select>
          </Field>
          <Field id="p-name" label="Your name"><input id="p-name" required className={inputCls} value={f.contact_name} onChange={set("contact_name")} autoComplete="name" data-testid="proposal-name" /></Field>
          <Field id="p-email" label="Work email"><input id="p-email" type="email" required className={inputCls} value={f.email} onChange={set("email")} autoComplete="email" data-testid="proposal-email" /></Field>
          <Field id="p-phone" label="Phone (optional)"><input id="p-phone" className={inputCls} value={f.phone} onChange={set("phone")} autoComplete="tel" data-testid="proposal-phone" /></Field>
          <Field id="p-opt" label="What do you need?">
            <select id="p-opt" className={inputCls} value={f.option} onChange={set("option")} data-testid="proposal-option">
              <option value="partnership">Custom Enterprise Partnership</option><option value="customized">Customized Experience (help me customize)</option>
            </select>
          </Field>
          <Field id="p-size" label="Estimated participants"><input id="p-size" className={inputCls} value={f.participants_estimate} onChange={set("participants_estimate")} placeholder="e.g. 250 across 4 teams" data-testid="proposal-size" /></Field>
          <Field id="p-time" label="Timeline"><input id="p-time" className={inputCls} value={f.timeline} onChange={set("timeline")} placeholder="e.g. Q3, monthly sessions" data-testid="proposal-timeline" /></Field>
          <div className="sm:col-span-2"><Field id="p-depts" label="Departments or teams (optional)"><input id="p-depts" className={inputCls} value={f.departments} onChange={set("departments")} data-testid="proposal-depts" /></Field></div>
        </div>
        {cat && (
          <div className="bg-white border border-[#ddd5c9] rounded-lg p-6 space-y-5">
            <fieldset><legend className="text-sm font-semibold">Programs of interest</legend>
              <div className="mt-2 grid sm:grid-cols-2 gap-2">{cat.programs.map((p) => (
                <label key={p.id} className="flex items-center gap-2 text-sm"><input type="checkbox" className="accent-[#7a1010]" checked={f.program_interest.includes(p.id)} onChange={() => toggle("program_interest", p.id)} data-testid={`proposal-program-${p.id}`} />{p.name}{p.status !== "available" ? " (in development)" : ""}</label>
              ))}</div>
            </fieldset>
            <fieldset><legend className="text-sm font-semibold">Learning objectives</legend>
              <div className="mt-2 grid sm:grid-cols-3 gap-2">{cat.objectives.map((o) => (
                <label key={o.id} className="flex items-center gap-2 text-sm"><input type="checkbox" className="accent-[#7a1010]" checked={f.objectives.includes(o.id)} onChange={() => toggle("objectives", o.id)} data-testid={`proposal-obj-${o.id}`} />{o.label}</label>
              ))}</div>
            </fieldset>
            <Field id="p-details" label="Anything else we should know?" hint="Goals, mission, accessibility needs, or procurement requirements. Please don't include confidential or sensitive information.">
              <textarea id="p-details" rows={5} maxLength={3000} className={inputCls} value={f.details} onChange={set("details")} data-testid="proposal-details" />
            </Field>
          </div>
        )}
        {err && <p role="alert" className="text-sm font-semibold text-[#7a1010]" data-testid="proposal-error">{err}</p>}
        <Btn type="submit" disabled={busy} data-testid="proposal-submit">{busy ? "Sending…" : "Send request"}</Btn>
      </form>
    </EntShell>
  );
}
